import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ObjectTypes } from '@fi-shared/types';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IChecklistSchedule, IChecklistTimeInterval } from '@fi-shared/checklist/checklist-v2';
import { FiChecklistScheduleType } from '../fi-checklist-schedule-type';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-checklist2-schedule-edit-general-info',
    templateUrl: './general-info.component.html',
    styleUrls: ['./general-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-schedule-edit-general-info' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiChecklist2ScheduleEditGeneralInfoComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiChecklist2ScheduleEditGeneralInfoComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiChecklist2ScheduleEditGeneralInfoComponent
    extends ControlValueAccessorValidatorBase<IChecklistSchedule>
    implements OnInit, OnDestroy
{
    @Input() selectedChecklist: INameIdObj;
    @Input() lightWeightChecklists: INameIdObj[];
    @Input() selectedContentLanguageId: number;
    @Input() triggerChange$: Observable<void> = new Observable<void>();
    @ViewChild('publicationDataModel') publicationDataModel: NgModel;
    ObjectTypes = ObjectTypes;
    minStartDate: Date;

    protected disabledChecklistIds: number[] = [];
    protected availableChecklists: INameIdObj[];

    constructor(
        injector: Injector,
        private fiChecklist2ScheduleIntervalService: FiChecklist2ScheduleIntervalService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.setStartMinDate();
        this.triggerChange$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.cdr.markForCheck());
        this.setAvailableChecklists();
    }

    private setStartMinDate() {
        this.minStartDate = this.checklistSchedule.Id ? new Date(this.checklistSchedule.CreatedOn) : new Date();
    }

    get checklistSchedule(): IChecklistSchedule {
        return this.ngModel;
    }

    protected setAvailableChecklists() {
        this.availableChecklists = [...this.lightWeightChecklists];
        if (
            isDefined(this.selectedChecklist) &&
            !this.availableChecklists.find((item) => item.Id === this.selectedChecklist.Id)
        ) {
            this.availableChecklists.unshift(this.selectedChecklist);
            this.disabledChecklistIds.push(this.selectedChecklist.Id);
        }
    }

    isChecklistScheduleNameHidden(checklistScheduleName: IContentLanguageLabelModel): boolean {
        return checklistScheduleName.ContentLanguageId !== this.selectedContentLanguageId;
    }

    // isChecklistScheduleNameRequired(): boolean {
    //     return !this.checklistSchedule.Names.find((checklistScheduleName) => checklistScheduleName.Value);
    // }

    isChecklistDisabled() {
        return this.ngModel.HasAnswers;
    }

    isChecklistRequired(): boolean {
        return this.checklistSchedule.PublicationSetting?.IsShared;
    }

    onChecklistPublicationSettingsChange() {
        if (!this.checklistSchedule.PublicationSetting && this.checklistSchedule.PublicationSetting?.IsShared)
            this.checklistSchedule.ChecklistId = undefined;
        this.doPropagateChanges();
    }

    getAvailablePublicationInterval(): { startDateStr: string; endDateStr: string } {
        if (
            this.checklistSchedule.ScheduleType === FiChecklistScheduleType.OneTime ||
            this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(this.checklistSchedule.TimeIntervalType)
        ) {
            const interval: IChecklistTimeInterval = this.checklistSchedule.TimeIntervals[0];
            const intervalStart: Date = this.fiChecklist2ScheduleIntervalService.convertIntervalTimeDateToDate(
                interval.Start,
            );
            const intervalEnd: Date = this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(
                this.checklistSchedule.TimeIntervalType,
            )
                ? this.fiChecklist2ScheduleIntervalService.getAuditOnDemandEndDate(interval)
                : this.fiChecklist2ScheduleIntervalService.convertIntervalTimeDateToDate(interval.End);

            return {
                startDateStr: intervalStart.toString(),
                endDateStr: intervalEnd.toString(),
            };
        }
    }

    doValidate(): ValidationErrors {
        return {
            publicationMinRangeFit: this.publicationDataModel?.errors?.publicationMinRangeFit,
        };
    }

    ngOnDestroy$: Subject<void> = new Subject<void>();

    isChecklistDisabledFn: FiDropdownItemDisabledFn<INameIdObj> = (item) => this.disabledChecklistIds.includes(item.Id);

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
